import { useEffect, useState } from "react";
import { Button, Flex, Pagination, Popconfirm, Table, TableColumnsType, TableProps } from "antd";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { mdlTablePagination } from "../../models/ui-models/TablePagination";
import dayjs from "dayjs";
import { mdlStopFreeSale } from "../../models/domain/StopFreeSale";
import { enmStopFreeSale } from "../../models/enums/StopFreeSale";
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faFilePdf, faPencil, faPrint, faTrash } from "@fortawesome/free-solid-svg-icons";

export type StopFreeSaleTableProps = {
  dataSource?: mdlStopFreeSale[],
  onTableParamsChange?: Function;
  onPaginationChange?: Function;
  pagination: mdlTablePagination;
  expandRowRenderer?: Function;
  onRowSelection?: Function;
  selectedRowKeys?: number[];
  selections?: mdlStopFreeSale[];
  tableTools?: JSX.Element;
  onEmailClick?: Function;
  onPrintClick?: Function;
  onPDFClick?: Function;
  onEditClick?: Function;
  onDelete?: Function;
  onCancelOrNew?: Function;
  unVisibleColumns?: string[]
}

export const StopFreeSaleTable = (props: StopFreeSaleTableProps) => {
  const { t } = useTranslation();
  const [source, setSource] = useState<mdlStopFreeSale[]>();
  useEffect(() => {
    if (props.dataSource) {
      var data = _.cloneDeep(props.dataSource);
      _.each(data, function (d, i) {
        d.key = i;
        _.each(d.details, function (detail, cIndex) {
          detail.key = cIndex;
        })
      })
      setSource(props.dataSource);
    }
  }, [props.dataSource]);

  const onEditClick = (e: mdlStopFreeSale) => {
    props.onEditClick && props.onEditClick(e)
  }

  const onEmailClick = (e: mdlStopFreeSale) => {
    props.onEmailClick && props.onEmailClick(e.id)
  }

  const onPrintClick = (e: mdlStopFreeSale) => {
    props.onPrintClick && props.onPrintClick(e.id)
  }

  const onPDFClick = (e: mdlStopFreeSale) => {
    props.onPDFClick && props.onPDFClick(e.id)
  }

  const onDelete = (e: mdlStopFreeSale) => {
    props.onDelete && props.onDelete(e.id)
  }

  const onCancelOrNew = (e: mdlStopFreeSale) => {
    props.onCancelOrNew && props.onCancelOrNew(e.id)
  }

  const baseColumns: TableColumnsType<mdlStopFreeSale> = [
    {
      title: t("stopFreeSaleTable.type"),
      width: 280,
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("type")),
      key: 'type',
      filters: [
        { text: t("stopFreeSaleTable.stopSale"), value: enmStopFreeSale.StopSale },
        { text: t("stopFreeSaleTable.freeSale"), value: enmStopFreeSale.FreeSale },
      ],
      render: (e: mdlStopFreeSale) => {
        return e.typeText
      }
    },
    {
      title: t("stopFreeSaleTable.sendDate"),
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("type")),
      key: 'sendDate',
      render: (e: mdlStopFreeSale) => {
        return dayjs(e.sendDate, "DD-MM-YYYY").format("DD-MM-YYYY")
      }
    },
    {
      title: t("stopFreeSaleTable.hotelName"),
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("hotelName")),
      width: 280,
      dataIndex: 'hotelName',
      key: 'hotelName',
    },
    {
      title: t("stopFreeSaleTable.mailSubject"),
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("mailSubject")),
      dataIndex: 'stopFreeName',
      key: 'stopFreeName',
    },
    {
      title: t("stopFreeSaleTable.startDate"),
      key: 'startDate',
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("startDate")),
      render: (e: mdlStopFreeSale) => {
        return dayjs(e.startDate, "DD-MM-YY").format("DD-MM-YYYY")
      }
    },
    {
      title: t("stopFreeSaleTable.endDate"),
      key: 'endDate',
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("endDate")),
      render: (e: mdlStopFreeSale) => {
        return dayjs(e.endDate, "DD-MM-YY").format("DD-MM-YYYY")
      }
    },
    {
      title: t("stopFreeSaleTable.status"),
      key: 'status',
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("status")),
      render: (e: mdlStopFreeSale) => {
        return props.onEditClick ? e.statusText : e.status == "Y" ? <span className={e.isSend == 1 ? "text-warning" : "text-primary"}>{e.isSendText}</span> : <span className={e.status == "I" ? "text-danger" : "text-primary"}>{e.statusText}</span>
      }
    },
    {
      title: t("stopFreeSaleTable.actions"),
      hidden: (props.unVisibleColumns && props.unVisibleColumns.includes("actions")),
      key: 'actions',
      render: (e: mdlStopFreeSale) => {
        return <>
          <Flex gap="small" wrap justify="center">
            {
              props.onEditClick &&
              <Button
                onClick={() => onEditClick(e)}
                className="btn btn-sm btn-primary" size="middle"
              >
                <FontAwesomeIcon icon={faPencil} />
                {t("stopFreeSaleTable.edit")}
              </Button>
            }
            {
              props.onEmailClick &&
              <Button
                onClick={() => onEmailClick(e)}
                className="btn btn-sm btn-success" size="middle"
              >
                <FontAwesomeIcon icon={faEnvelope} />
                {t("stopFreeSaleTable.email")}
              </Button>
            }
            {
              props.onPrintClick &&
              <Button
                onClick={() => onPrintClick(e)}
                className="btn btn-sm btn-dark" size="middle"
              >
                <FontAwesomeIcon icon={faPrint} />
                {t("stopFreeSaleTable.print")}
              </Button>
            }
            {
              props.onPDFClick &&
              <Button
                onClick={() => onPDFClick(e)}
                className="btn btn-sm btn-secondary" size="middle"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                {t("stopFreeSaleTable.pdf")}
              </Button>
            }
            {
              props.onDelete &&
              <Button
                onClick={() => onDelete(e)}
                className="btn btn-sm btn-danger" size="middle"
              >
                <FontAwesomeIcon icon={faTrash} />
                {t("stopFreeSaleTable.delete")}
              </Button>
            }
            {
              e.status != "Y" && props.onCancelOrNew &&
              <Popconfirm
                title={t("common.confirm")}
                description={t("stopFreeSaleTable.reSendInfo")}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                onConfirm={() => props.onCancelOrNew ? props.onCancelOrNew(e) : undefined}
              >
                <Button type="primary" className="btn btn-success">
                  {t("stopFreeSaleTable.new")}
                </Button>
              </Popconfirm>
            }
            {
              e.status == "Y" && props.onCancelOrNew &&
              <Popconfirm
                title={t("common.confirm")}
                description={t("stopFreeSaleTable.cancelInfo")}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                onConfirm={() => props.onCancelOrNew ? props.onCancelOrNew(e) : undefined}
              >
                <Button type="primary" danger>
                  {t("stopFreeSaleTable.cancel")}
                </Button>
              </Popconfirm>
            }
          </Flex>
        </>
      }
    }
  ];

  const expandedRowRenderer = (detail?: string) => (
    props.expandRowRenderer && props.expandRowRenderer(detail)
  );

  const handleTableChange: TableProps<mdlStopFreeSale>['onChange'] = (pagination, filters, sorter) => {
    props.onTableParamsChange && props.onTableParamsChange(filters);
  }


  const getScreenSize = () => {
    return `${(window.screen.height * 65) / 100}px`;
  }


  return (
    <Flex gap="middle" align="start" vertical>
      <Table<mdlStopFreeSale>
        style={{ maxHeight: getScreenSize() }}
        rowKey={(item: mdlStopFreeSale) => item.id ?? ""}
        onChange={handleTableChange}
        columns={baseColumns}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (expandedRowRenderer(record.id))
        }}
        size="small"
        dataSource={source}
        scroll={{ x: 'max-content' }}
      />
      <Flex style={{ width: "100%" }} justify="space-between" wrap gap={"15px"}>
        <Pagination
          showSizeChanger
          current={props.pagination.current}
          onChange={(page: number, pageSize: number) => props.onPaginationChange && props.onPaginationChange(page, pageSize)}
          defaultCurrent={1}
          pageSize={props.pagination.pageSize}
          total={props.pagination.total}
        />
        {
          props.tableTools
          &&
          props.tableTools
        }
      </Flex>

    </Flex >
  );
};
